// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Opbygning library and what a pick does. ReUseX owns the library and
 * the tonnes now (schema v37): `GET /buildups` lists it, a type stores its
 * choice in `buildup_id` and the server sends `mass_calc_t` / `mass_source`
 * (`hasServerMass`). Against a server before that this module is still the
 * whole story — the embedded copy (`buildupLibrary.ts`) gives the EAK
 * suggestion and the tonnes (areal × tykkelse × densitet, areal × kg/m², or
 * volumen × densitet for a slab counted in m³), and the chosen build-up
 * persists as the user column `Opbygning` (decision 6: `POST
 * /resources/columns` on first use, then `PATCH /resources/{code}` on every
 * part of the type — user columns are part-scoped; a name the column does
 * not list yet is added to its options first). The other layers can each
 * become a type of their own ("Opret som egen type"). Pure: no React, no DOM.
 */

import type {
  Buildup,
  BuildupCategory,
  BuildupLayer,
  ResourceColumnCreate,
  ResourceKey,
  SurveyTypeCreate,
  SurveyTypePatch,
  SurveyType,
  Treatment,
} from '../api/types';
import { formatNumber } from '../kortlaegning/vocab';
import { LIBRARY_BUILDUPS } from './buildupLibrary';

export type { Buildup, BuildupCategory };
export type Layer = BuildupLayer;

/** The embedded library: the fallback for a server without `GET /buildups`. */
export const BUILDUPS: readonly Buildup[] = LIBRARY_BUILDUPS;

/** The server computes the type's tonnes (`mass_source` is sent, ReUseX schema v37). */
export function hasServerMass(type: Pick<SurveyType, 'mass_source'>): boolean {
  return type.mass_source !== undefined;
}

/** The user column the choice is stored in (decision 6), found by this label. */
export const BUILDUP_COLUMN_NAME = 'Opbygning';

export function buildupByName(name: string | null | undefined, library: readonly Buildup[] = BUILDUPS): Buildup | null {
  if (!name) return null;
  const n = name.trim();
  return library.find((b) => b.name === n) ?? null;
}

export function buildupById(id: string | null | undefined, library: readonly Buildup[] = BUILDUPS): Buildup | null {
  if (!id) return null;
  return library.find((b) => b.id === id) ?? null;
}

/**
 * The type's build-up: its `buildup_id` in the library when the server
 * keeps it, else the one the `Opbygning` column names (an older server).
 */
export function currentBuildup(
  type: Pick<SurveyType, 'buildup_id' | 'mass_source'>,
  library: readonly Buildup[],
  columnValue: string | null,
): Buildup | null {
  if (hasServerMass(type)) return buildupById(type.buildup_id, library);
  return buildupByName(columnValue, library);
}

/**
 * A build-up the old `Opbygning` column still names on a server that keeps
 * `buildup_id` instead, while the type has none yet: offered as "Brug".
 */
export function legacyColumnBuildup(
  type: Pick<SurveyType, 'buildup_id' | 'mass_source'>,
  library: readonly Buildup[],
  columnValue: string | null,
): Buildup | null {
  if (!hasServerMass(type) || type.buildup_id) return null;
  return buildupByName(columnValue, library);
}

/**
 * Name keywords → category, most specific first: a word *containing* one of
 * `contains`, or *starting with* one of `starts` ("Tagpap" is tag, "Etagedæk"
 * is dæk, not tag). Then the BIM7AA group (21 ydervægge, 22 indervægge,
 * 23 dæk, 27 tage).
 */
const CATEGORY_RULES: readonly {
  cat: BuildupCategory;
  contains?: readonly string[];
  starts?: readonly string[];
  bim: string;
}[] = [
  { cat: 'indervaeg', contains: ['indervæg', 'skillevæg', 'gipsvæg'], bim: '22' },
  { cat: 'ydervaeg', contains: ['ydervæg', 'facade', 'mur'], bim: '21' },
  { cat: 'daek', contains: ['dæk', 'gulv'], bim: '23' },
  { cat: 'tag', starts: ['tag'], bim: '27' },
];

/**
 * The build-up category a type belongs to: the model's (`element_category`,
 * set by `core::measure_survey`), else by name, then BIM7AA; null when unknown.
 */
export function categoryOf(type: Pick<SurveyType, 'name' | 'bim7aa_code' | 'element_category'>): BuildupCategory | null {
  if (type.element_category) return type.element_category;
  const words = type.name.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  for (const rule of CATEGORY_RULES) {
    if (rule.contains?.some((k) => words.some((w) => w.includes(k)))) return rule.cat;
    if (rule.starts?.some((k) => words.some((w) => w.startsWith(k)))) return rule.cat;
  }
  const group = /^(\d{2})\b/.exec(type.bim7aa_code.trim())?.[1];
  return CATEGORY_RULES.find((r) => r.bim === group)?.cat ?? null;
}

/** The build-ups the picker offers a type: its category's, or all of them when the category is unknown. */
export function candidates(
  type: Pick<SurveyType, 'name' | 'bim7aa_code' | 'element_category'>,
  library: readonly Buildup[] = BUILDUPS,
): Buildup[] {
  const cat = categoryOf(type);
  return library.filter((b) => cat === null || b.category === cat);
}

/** Whether a type can carry a build-up at all: stk types are counted, not measured. */
export function takesBuildup(type: Pick<SurveyType, 'unit'>): boolean {
  return type.unit !== 'stk';
}

export function roundTenth(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Tonnes of one layer for a quantity in `unit` (m² or m³). */
export function layerTonnes(layer: Layer, quantity: number, unit: string): number {
  if (layer.kg_m2 !== undefined) return (quantity * layer.kg_m2) / 1000;
  const rho = layer.rho ?? 0;
  if (unit === 'm³') return (quantity * rho) / 1000;
  return (quantity * (layer.t ?? 0) * rho) / 1000;
}

/** The main layer's tonnes for the type's quantity, or null (stk, or no build-up). */
export function calcTonnes(type: Pick<SurveyType, 'unit' | 'quantity'>, buildup: Buildup | null): number | null {
  if (!buildup || !takesBuildup(type)) return null;
  return layerTonnes(buildup.main, type.quantity || 0, type.unit);
}

/** How the tonnes were computed, for the Tons row's sub line. */
export function calcMethod(type: Pick<SurveyType, 'unit'>, buildup: Buildup): string {
  if (buildup.main.kg_m2 !== undefined) return 'areal × kg/m²';
  if (type.unit === 'm³') return 'volumen × densitet';
  return 'areal × tykkelse × densitet';
}

function layerLabel(l: Layer): string {
  if (l.kg_m2 !== undefined) return `${formatNumber(l.kg_m2)} kg/m² ${l.material}`;
  if ((l.t ?? 0) >= 1) return `${l.material}, massiv`;
  return `${Math.round((l.t ?? 0) * 1000)} mm ${l.material}`;
}

/** The layers in one line: "108 mm tegl · 75 mm mineraluld · 100 mm letbeton". */
export function layerText(b: Buildup): string {
  return [b.main, ...b.others].map(layerLabel).join(' · ');
}

export function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** A split-off layer's line: "Mineraluld 75 mm · 2,1 t · 17.06.04". */
export function otherLayerLine(layer: Layer, type: Pick<SurveyType, 'unit' | 'quantity'>): string {
  const thick = layer.kg_m2 !== undefined ? `${formatNumber(layer.kg_m2)} kg/m²` : `${Math.round((layer.t ?? 0) * 1000)} mm`;
  const t = roundTenth(layerTonnes(layer, type.quantity || 0, type.unit));
  return `${capitalize(layer.material)} ${thick} · ${formatNumber(t)} t · ${layer.eak}`;
}

// ---------------------------------------------------------------- plans --

export interface PickPlan {
  /** The type patch the pick implies (EAK when empty; tonnes when missing or still computed). */
  patch: SurveyTypePatch;
  /** What was filled, for the toast ("EAK og tons udfyldt"). */
  filled: ('EAK' | 'tons')[];
}

/**
 * What picking `next` does to the type: fills an empty EAK with the main
 * layer's code, and sets the tonnes when they are missing or were computed
 * from the previous build-up (`|mass − calc| < 0.05`), never over a typed
 * value.
 */
export function pickPlan(type: SurveyType, previous: Buildup | null, next: Buildup): PickPlan {
  const patch: SurveyTypePatch = {};
  const filled: PickPlan['filled'] = [];
  if (!type.eak_code.trim()) {
    patch.eak_code = next.main.eak;
    filled.push('EAK');
  }
  const prevCalc = calcTonnes(type, previous);
  const wasComputed = type.mass_t !== null && prevCalc !== null && Math.abs(type.mass_t - roundTenth(prevCalc)) < 0.051;
  const calc = calcTonnes(type, next);
  if (calc !== null && (type.mass_t === null || wasComputed)) {
    patch.mass_t = roundTenth(calc);
    filled.push('tons');
  }
  return { patch, filled };
}

/** The toast after a pick. */
export function pickToast(plan: PickPlan): string {
  return plan.filled.length ? `Opbygning valgt · ${plan.filled.join(' og ')} udfyldt` : 'Opbygning valgt';
}

/**
 * A pick on a server that computes the tonnes: one PATCH with `buildup_id`
 * (null clears it) and an empty EAK filled with the main layer's code. The
 * tonnes are never written — the server recalculates them, and a typed
 * override stays until "Brug beregnet" clears it.
 */
export function serverPickPlan(type: Pick<SurveyType, 'eak_code'>, next: Buildup | null): PickPlan {
  const patch: SurveyTypePatch = { buildup_id: next ? next.id : null };
  const filled: PickPlan['filled'] = [];
  if (next && !type.eak_code.trim()) {
    patch.eak_code = next.main.eak;
    filled.push('EAK');
  }
  return { patch, filled };
}

/** The toast after a server pick, from the type the server answered with. */
export function serverPickToast(plan: PickPlan, updated: Pick<SurveyType, 'mass_calc_t' | 'mass_source'>): string {
  if (plan.patch.buildup_id === null) return 'Opbygning fjernet';
  const bits = ['Opbygning valgt'];
  if (plan.filled.includes('EAK')) bits.push('EAK udfyldt');
  const calc = updated.mass_calc_t;
  if (calc !== null && calc !== undefined) {
    bits.push(`${formatNumber(roundTenth(calc))} t beregnet${updated.mass_source === 'override' ? ' (din indtastede vægt gælder stadig)' : ''}`);
  }
  return bits.join(' · ');
}

/** A layer already split off: some type carries its name. */
export function layerExists(types: readonly Pick<SurveyType, 'name'>[], layer: Layer): boolean {
  const name = (layer.name ?? '').trim();
  return name !== '' && types.some((t) => t.name.trim() === name);
}

const LAYER_TREATMENT: Record<string, Treatment> = {
  '17.06.04': 'genanvendelse',
  '17.01.01': 'genanvendelse',
  '17.02.01': 'genanvendelse',
};

export interface SplitPlan {
  /** `POST /survey/types`, tonnes and note included (gap 12, ruxd-6tz). */
  create: SurveyTypeCreate;
  /** The manual part's name (`POST /resources {type_id, name}`). */
  partName: string;
  /** The part's quantity (`PATCH /survey/parts/{code}`): the source type's quantity. */
  quantity: number;
}

/**
 * The fields of a create the server did not take — an older ruxd ignores
 * note/mass_t/confidence/starred on `POST /survey/types` — as the follow-up
 * PATCH; null when it took them all.
 */
export function createFollowUp(sent: SurveyTypeCreate, created: SurveyType): SurveyTypePatch | null {
  const patch: SurveyTypePatch = {};
  if (sent.note !== undefined && created.note !== sent.note) patch.note = sent.note;
  if (sent.mass_t !== undefined && created.mass_t !== sent.mass_t) patch.mass_t = sent.mass_t;
  if (sent.confidence !== undefined && created.confidence !== sent.confidence) patch.confidence = sent.confidence;
  if (sent.starred !== undefined && created.starred !== sent.starred) patch.starred = sent.starred;
  return Object.keys(patch).length > 0 ? patch : null;
}

/** "Opret som egen type" for `b.others[index]` of a type. */
export function splitLayerPlan(type: SurveyType, b: Buildup, index: number): SplitPlan | null {
  const layer = b.others[index];
  if (!layer) return null;
  const name = layer.name?.trim() || capitalize(layer.material);
  return {
    create: {
      name,
      eak_code: layer.eak,
      unit: type.unit,
      treatment: LAYER_TREATMENT[layer.eak] ?? 'genanvendelse',
      mass_t: roundTenth(layerTonnes(layer, type.quantity || 0, type.unit)),
      note: `Oprettet fra opbygningen ${b.name}`,
    },
    partName: name,
    quantity: type.quantity,
  };
}

// --------------------------------------------------------------- column --

/** The `Opbygning` user column's key in the catalogue, or null before first use. */
export function buildupColumnKey(catalogue: readonly ResourceKey[]): ResourceKey | null {
  const want = BUILDUP_COLUMN_NAME.toLowerCase();
  return catalogue.find((k) => k.id.startsWith('col:') && k.label.trim().toLowerCase() === want) ?? null;
}

/** The body of the first-use `POST /resources/columns`. */
export function buildupColumnBody(): ResourceColumnCreate {
  return { name: BUILDUP_COLUMN_NAME, type: 'select', options: BUILDUPS.map((b) => b.name) };
}

/** `col:<id>` → `<id>`; null for any other key id. */
export function columnIdOf(keyId: string): string | null {
  return keyId.startsWith('col:') ? keyId.slice(4) : null;
}

/**
 * The options a select column needs before `name` can be stored, or null
 * when it can already (a text column, or the name is listed).
 */
export function optionsWith(key: Pick<ResourceKey, 'data_type' | 'options'>, name: string): string[] | null {
  if (key.data_type !== 'enum') return null;
  return key.options.includes(name) ? null : [...key.options, name];
}
